/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, forwardRef, useContext, useEffect, useState } from 'react';
import font from '../assets/GTWalsheimProRegular.ttf';
import { useCampaignContext } from './CampaignContext';
import { TVJSON, OOHPRINT } from 'customHooks/useDataQuery';
import { orderList2 } from 'components/newSetup/SortingPlaceHolderList';
import { StyleSheet, Font } from '@react-pdf/renderer';
import { API } from 'aws-amplify';
import { getQueries, getTemplateById } from 'graphql/queries';
import { ClientsInput, DashboardTypes, DataSourcesInfo, GetQueriesQuery, GetTemplateByIdQuery, UserQuery } from 'API';

import pptxgen from 'pptxgenjs';

import omdlogo from '../assets/lightomd.png';
import phdlogo from '../assets/ppt_assets/PHD_pptlogo.png';
import hslogo from '../assets/lightheartScienceNoBckg.png';

import { useSlideDefinition } from 'methods/masterSlidesDefinitions';
import { useClientContext } from './ClientContext';
import { useVideoParams } from 'customHooks/useParameters';
import APIContext from './APIContext';

const InputContext = createContext({
  TVKPI: {} as TVJSON,
  RADIOKPI: {} as TVJSON,
  OOHKPI: {} as OOHPRINT,
  PRINTKPI: {} as OOHPRINT,
  CINEMAKPI: {} as OOHPRINT,
  setNetworkList: (arg: any) => {},
  networkList: [],
  setTVKPI: (arg: any) => {},
  setRADIOKPI: (arg: any) => {},
  setOOHKPI: (arg: any) => {},
  setPRINTKPI: (arg: any) => {},
  setCINEMAKPI: (arg: any) => {},
  order2Table: [] as orderList2[],
  setOrder2Table: (arg: any) => {},
  order2Chart: [] as orderList2[],
  setOrder2Chart: (arg: any) => {},
  setCapture: (arg: boolean) => {},
  capture: false,
  updatePPT: (arg: any) => {},
  storedQueriesOnCurrentDash: [] as UserQuery[],
  setStoredQueriesOnCurrentDash: (arg: any) => {},
  // childCampaignIndex: 0,
  // setChildCampaignIndex: (arg: number) => {},
  dataSourceLength: 0,
  setDataSourceLength: (arg: number) => {},
  // deleteUmbrellaCampaignModal: false,
  // setDeleteUmbrellaCampaignModal: (arg: boolean) => {},
  organisationReportActive: false,
  setOrganisationReportViewActive: (arg: boolean) => {},
  videoDashboardView: 'TV',
  setVideoDashboardView: (arg: string) => {},
  contextedDataSources: {} as DataSourcesInfo[] | undefined,
  setContextedDataSources: (arg: DataSourcesInfo[] | undefined) => {},
  creativeLibrarySections: [] as creativeLibrarySectionType[],
  setCreativeLibrarySections: (arg: creativeLibrarySectionType[]) => {},
  convertBackendCreativeToLocal: async (arg: creativesBackendType) => ({}) as creativesDataType,
  detectFileTypeFromName: (fileName: string) => '' as string,
});

export type PDFelementType = {
  placeholder: string | undefined;
  image: string | undefined;
  title: string;
  comment: string | undefined;
  captured: boolean;
  columns: any;
  data: any;
};

export type creativesMetricsType = {
  position: number;
  label: string;
  value: number;
  column?: string; // Reference to original data column for auto-updates
  decimals?: number; // Display precision (default: 2)
};

export type creativesDataType = {
  id: string;
  position: number;
  file: string; // Local file data (base64 or blob URL) - converted from creativeOutputLocation
  fileType: string; // MIME type for proper file preview (e.g., 'image/jpeg', 'video/mp4')
  creativeID: string;
  creativeName: string;
  metrics: Array<creativesMetricsType>;
  // Backend still expects creativeOutputLocation, but we store file locally
};

// Backend type - what we receive from/send to backend
export type creativesBackendType = {
  id: string;
  position: number;
  creativeOutputLocation: string;
  creativeID: string;
  creativeName: string;
  metrics: Array<creativesMetricsType>;
};

export type creativeLibrarySectionType = {
  position: string;
  dataSourceName: string;
  data: any[];
  outputLocation: string | undefined;
  title?: string | undefined;
  creatives: creativesDataType[];
  autoOpenAddModal?: boolean; // Flag za automatsko otvaranje Add Creative modal
};

export const InputContextProvider = (props: any) => {
  const [dataSourceLength, setDataSourceLength] = useState<number>(0);
  const [deleteUmbrellaCampaignModal, setDeleteUmbrellaCampaignModal] = useState(false);
  const [organisationReportActive, setOrganisationReportViewActive] = useState(false);
  const { activeCampaign, dashType, activeSubCampaign } = useCampaignContext();
  const [TVKPI, setTVKPI] = useState({} as TVJSON);
  const [RADIOKPI, setRADIOKPI] = useState({} as TVJSON);
  const [OOHKPI, setOOHKPI] = useState({} as OOHPRINT);
  const [PRINTKPI, setPRINTKPI] = useState({} as OOHPRINT);
  const [CINEMAKPI, setCINEMAKPI] = useState({} as OOHPRINT);
  const [networkList, setNetworkList] = useState<any>([]);
  const [order2Table, setOrder2Table] = useState<orderList2[]>([]);
  const [order2Chart, setOrder2Chart] = useState<orderList2[]>([]);
  const [storedQueriesOnCurrentDash, setStoredQueriesOnCurrentDash] = useState<Array<UserQuery>>([]);
  const [videoSource, setVideoSource, deleteVideoSource] = useVideoParams();
  const [contextedDataSources, setContextedDataSources] = useState<DataSourcesInfo[] | undefined>();
  const [creativeLibrarySections, setCreativeLibrarySections] = useState<creativeLibrarySectionType[]>([]);
  // Get environment from APIContext (not used in this simplified version)
  // const { getEnvironment } = useContext(APIContext);

  // Helper function to detect file type from filename
  const detectFileTypeFromName = (fileName: string): string => {
    if (!fileName) return 'application/octet-stream';

    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'mp4':
        return 'video/mp4';
      case 'mov':
        return 'video/quicktime';
      case 'avi':
        return 'video/x-msvideo';
      case 'pdf':
        return 'application/pdf';
      default:
        return 'application/octet-stream';
    }
  };

  // Helper function to convert backend creative to local creative
  const convertBackendCreativeToLocal = async (backendCreative: creativesBackendType): Promise<creativesDataType> => {
    // No longer convert to blob URL, keep S3 file name for lazy loading
    const fileType = detectFileTypeFromName(backendCreative.creativeOutputLocation);

    const localCreative: creativesDataType = {
      id: backendCreative.id,
      position: backendCreative.position,
      file: backendCreative.creativeOutputLocation, // Keep S3 file name instead of converting to blob URL
      fileType: fileType,
      creativeID: backendCreative.creativeID,
      creativeName: backendCreative.creativeName,
      metrics: backendCreative.metrics,
    };

    return localCreative;
  };
  useEffect(() => {
    // This useEffect can be used for debugging creativeLibrarySections if needed
  }, [creativeLibrarySections]);

  // Test function to simulate backend creative conversion
  const testConvertBackendCreative = async () => {
    const mockBackendCreative: creativesBackendType = {
      id: '901.0',
      position: 1,
      creativeOutputLocation: 'test_file_name.jpg',
      creativeID: 'CR001',
      creativeName: 'Test Creative',
      metrics: [
        { position: 1, label: 'Impressions', value: 1000 },
        { position: 2, label: 'Clicks', value: 50 },
      ],
    };

    const localCreative = await convertBackendCreativeToLocal(mockBackendCreative);
    console.log('Converted creative:', localCreative);
  };

  // videoDashboardView je sada ovisan o videoSource URL parametru
  // Početna vrijednost se postavlja na osnovu URL parametra umjesto fiksno na 'TV'
  const [videoDashboardView, setVideoDashboardView] = useState<string>(() => {
    // Ako imamo videoSource u URL-u, koristi ga kao početnu vrijednost
    return videoSource !== 'none' ? videoSource : 'TV';
  });

  // useEffect za sinkronizaciju videoDashboardView s videoSource URL parametrom
  useEffect(() => {
    if (dashType === 'TV' && activeCampaign) {
      if (videoSource !== 'none') {
        // Ako imamo videoSource u URL-u, koristi ga (samo ako se razlikuje od trenutne vrijednosti)
        if (videoDashboardView !== videoSource) {
          setVideoDashboardView(videoSource);
        }
        const isVideoSourceFromURLValid = activeCampaign.dashboardType?.includes(videoSource as DashboardTypes);
        if (!isVideoSourceFromURLValid) {
          const availableVideoType = activeCampaign.dashboardType?.find((type) => type === 'TV' || type === 'BVOD');
          setVideoSource(availableVideoType as string);
          setVideoDashboardView(availableVideoType as string);
        }
      } else {
        // Ako nema videoSource u URL-u, postavi prvi dostupni tip iz dashboardType
        const availableVideoType = activeCampaign.dashboardType?.find((type) => type === 'TV' || type === 'BVOD');
        if (availableVideoType && videoDashboardView !== availableVideoType) {
          setVideoSource(availableVideoType);
          setVideoDashboardView(availableVideoType);
        }
      }
    }
  }, [dashType, activeCampaign, videoSource, setVideoSource, videoDashboardView]);

  // Funkcija za promjenu videoDashboardView koja također ažurira URL
  const updateVideoDashboardView = (newView: string) => {
    setVideoDashboardView(newView);
    setVideoSource(newView);
  };
  let pres = new pptxgen();
  // const [bigO, setBigO] = useState<string>('');
  const { REACT_APP_AGENCY } = process.env;
  // const [backgroundImage, setbackgroundImage] = useState<string>('');

  const convertToBase64 = async (imageUrl: string, setBase64String: (base64: string) => void) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          const base64 = reader.result.replace('data:', '').replace(/^.+,/, '');

          setBase64String(base64);
        } else {
          console.error('Error: reader.result is not a string');
        }
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error converting image to base64:', error);
    }
  };
  // const logo = REACT_APP_AGENCY === "hs" ? convertToBase64(hslogo) : REACT_APP_AGENCY === "phd" ? convertToBase64(phdlogo) : omdlogo
  const [logo, setLogo] = useState<string>('');

  useEffect(() => {
    // convertToBase64(pptbackground, setbackgroundImage);
    // convertToBase64(omgO, setBigO);
    if (REACT_APP_AGENCY === 'hs') {
      convertToBase64(hslogo, setLogo);
    } else if (REACT_APP_AGENCY === 'phd') {
      convertToBase64(phdlogo, setLogo);
    } else {
      setLogo(omdlogo);
    }
  }, []);

  const [slideDef] = useSlideDefinition(REACT_APP_AGENCY as string);
  pres.layout = 'LAYOUT_WIDE';
  const date = new Date();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = date.getFullYear();
  const todayDate = `${day}-${month}-${year}`;

  Font.register({
    family: 'FamilyName',
    format: 'truetype',
    src: font,
  });

  const styles = StyleSheet.create({
    page: { padding: '20px' /* , fontFamily: "FamilyName"  */ },
    table: { width: 'auto' },
    coverPage: { display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' },
    coverText: {
      fontSize: 28,
      marginBottom: 20,
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    coverImage: { width: '100px', height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  });

  useEffect(() => {
    setNetworkList([]);
  }, [dashType, activeCampaign, activeSubCampaign]);

  const [pdfElements, setPdfElements] = useState<PDFelementType[]>([]);
  const arrayofSnapshots = [] as PDFelementType[];

  const [pptElements, setPPTElements] = useState<any[]>([{} as any]);
  const arrayofSnapshotsPPT = [] as any[];
  const updatePPT = (element: any) => {
    const found = pptElements.find((pptElement) => pptElement.placeholder === element.placeholder);
    if (found) {
      arrayofSnapshotsPPT.push(element);
      if (
        arrayofSnapshotsPPT.length > 0 &&
        (((dashType === 'DIGITAL' || dashType === 'INSOURCE_DATA') &&
          arrayofSnapshotsPPT.length === pptElements.length) ||
          (dashType === 'TV' && arrayofSnapshotsPPT.length === 7) ||
          (dashType === 'RADIO' && arrayofSnapshotsPPT.length === 7) ||
          (dashType === 'OOH' && arrayofSnapshotsPPT.length === 1) ||
          (dashType === 'PRINT' && arrayofSnapshotsPPT.length === 1) ||
          (dashType === 'CINEMA' && arrayofSnapshotsPPT.length === 1))
      ) {
        pres.defineSlideMaster(slideDef.chartSlide);
        pres.defineSlideMaster(slideDef.tableSlide);
        pres.defineSlideMaster(slideDef.titleSlide);

        const introSlide = pres.addSlide({ masterName: 'MASTER_SLIDE' });

        REACT_APP_AGENCY === 'omd' &&
          introSlide.addImage({
            data: REACT_APP_AGENCY === 'omd' ? logo : `image/png;base64,${logo}`,
            placeholder: 'logo',
          });

        arrayofSnapshotsPPT.forEach((element) => {
          if (element.columns === undefined) {
            if (
              element.placeholder !== 'OOH1.1' &&
              element.placeholder !== 'Cinema1.1' &&
              element.placeholder !== 'Print1.1'
            ) {
              const checkIfAllTypesAreTheSame = element.theData
                .map((e: any) => e.type)
                .every((val: any, i: any, arr: any) => val === arr[0]);
              const isAnyStacked = element.theData
                .map((e: any) => e.originalType)
                .some((val: any) => val === 'stacked');

              const allTypesAreTheSame = checkIfAllTypesAreTheSame;

              const options = {
                showTitle: false,
                title: '',
                chartArea: { fill: { color: REACT_APP_AGENCY === 'omd' ? '#fafafa' : '#F7F7F6' } },
                x: REACT_APP_AGENCY === 'omd' ? 4.5 : REACT_APP_AGENCY === 'phd' ? 4.3 : 4.7,

                y: 1.5,
                w: 8,
                h: 4.25,
                showLabel: false,
                showLeaderLines: false,
                showLegend: true,
                legendPos: 'b' as 'b' | 'l' | 'r' | 't' | 'tr' | undefined,
                showValue: true,
                dataLabelColor: '#8b8b8b',
                dataLabelFontSize: 10,
                catAxisLabelColor: '#8b8b8b',
                catAxisLabelFontSize: 10,
                serGridLine: { style: 'none' as 'none' | 'solid' | 'dash' | 'dot' | undefined },
                valGridLine: { style: 'none' as 'none' | 'solid' | 'dash' | 'dot' | undefined },
                valAxisLabelColor: '#8b8b8b',
                valAxisLabelFontSize: 10,
              };

              const slide = pres.addSlide({ masterName: 'MASTER_SLIDE_CHART' });
              slide.addText(element.title, { placeholder: 'title' });
              slide.addText(element.comment, { placeholder: 'comment' });
              const dataChart = [] as any;

              if (element.theData.length < 2) {
                const dataChartObject = {
                  name: element.theData[0].name,
                  labels: element.categories,
                  values: element.theData[0].data,
                };
                dataChart.push(dataChartObject);

                slide.addChart(element.theData[0].type === 'column' ? 'bar' : element.theData[0].type, dataChart, {
                  ...options,
                  chartColors:
                    element.theData[0].type === 'pie'
                      ? [
                          '#7D98AB',
                          '#A9C8C0',
                          '#DBBC8E',
                          '#C6ADAE',
                          '#698296',
                          '#A3B6C4',
                          '#E5D0AF',
                          '#C2D8D3',
                          '#95A7B5',
                          '#AE8A8C',
                        ]
                      : ['#7D98AB'],
                });
              } else if (element.theData.length > 1 && allTypesAreTheSame && !isAnyStacked) {
                const chD = element.theData.map((e: any, i: number) => {
                  return {
                    name: e.name,
                    labels: element.categories,
                    values: e.data,
                  };
                });

                slide.addChart(element.theData[0].type === 'column' ? 'bar' : element.theData[0].type, chD, {
                  ...options,
                  chartColors: [
                    '#7D98AB',
                    '#A9C8C0',
                    '#DBBC8E',
                    '#C6ADAE',
                    '#698296',
                    '#A3B6C4',
                    '#E5D0AF',
                    '#C2D8D3',
                    '#95A7B5',
                    '#AE8A8C',
                  ],
                });
              } else {
                const chD = element.theData.map((e: any, i: number) => {
                  return {
                    type:
                      e.type === 'column'
                        ? pres.ChartType.bar
                        : e.type === 'scatter'
                          ? 'line'
                          : pres.ChartType[e.type as keyof typeof pres.ChartType],
                    data: [
                      {
                        name: e.name,
                        labels: element.categories,
                        values: e.data,
                      },
                    ],
                    options:
                      i === 0
                        ? {
                            chartColors: [e.color],
                          }
                        : {
                            chartColors: e.marker ? [e.marker.lineColor] : [e.color],
                            secondaryValAxis: element.theData[0].originalType === 'stacked' ? false : true,
                            secondaryCatAxis: element.theData[0].originalType === 'stacked' ? false : true,
                            showLabel: false,
                            showLegend: true,
                            showValue: true,
                            catAxisLineShow: false,
                            serAxisLineShow: false,
                            dataLabelBkgrdColors: false,
                            padding: 10,
                          },
                  };
                });

                const opt =
                  element.theData.length === 1 || element.theData[0].originalType === 'stacked'
                    ? options
                    : element.theData.some((e: any) => e.type === 'scatter')
                      ? {
                          ...options,
                          lineSize: 0,
                          lineDataSymbolSize: 20,
                          lineDataSymbol: 'dash',
                          lineDataSymbolLineSize: 1,

                          lineDataSymbolLineColor:
                            REACT_APP_AGENCY === 'omd' ? '#FF0000' : REACT_APP_AGENCY === 'hs' ? '#2CB890' : '#38164f',

                          catAxes: [
                            { catAxisTitle: 'Primary Category Axis', catAxisHidden: false },
                            { catAxisTitle: 'Secondary Category Axis', catAxisHidden: true },
                          ],
                          valAxes: [
                            { valAxisTitle: 'Primary Value Axis', valAxisHidden: false },
                            { valAxisTitle: 'Secondary Value Axis', valAxisHidden: false },
                          ],
                        }
                      : {
                          ...options,
                          catAxes: [
                            { catAxisTitle: 'Primary Category Axis', catAxisHidden: false },
                            { catAxisTitle: 'Secondary Category Axis', catAxisHidden: true },
                          ],
                          valAxes: [
                            { valAxisTitle: 'Primary Value Axis', valAxisHidden: false },
                            { valAxisTitle: 'Secondary Value Axis', valAxisHidden: false },
                          ],
                        };

                slide.addChart(chD, opt as any);
              }
            } else {
              //TODO: THIS WORKS TO ADD CHART AS IMAGE TO PPT
              const slide = pres.addSlide();
              const base64image = element.image;
              var img = document.createElement('img');
              img.setAttribute('src', base64image);
              slide.addText(element.title, { x: 0, y: 0, w: '100%', h: 0.5, fontSize: 15 });
              slide.addImage({
                path: element.image,
                x: 0.6,
                y: 0.6,
                w: 12,
                h: element.ratioMultiplier /* , sizing: { type: "contain", w: 12, h: element.ratioMultiplier } */,
              });
            }
          } else {
            const nekiid = `id${element.placeholder.replace(/\./g, '0')}`;

            pres.tableToSlides(nekiid, {
              masterSlideName: 'MASTER_SLIDE_TABLE',
              // x: 0.5,
              y: 2.5,
              h: '90%',
              x: REACT_APP_AGENCY === 'omd' ? '35%' : 0.5,
              w: REACT_APP_AGENCY === 'omd' ? 8 : 12,

              addText: {
                text: element.title,
                options: {
                  placeholder: 'title',
                },
              },
            });
          }
        });
        pres.writeFile({
          fileName: `${activeCampaign?.campaign} (${activeCampaign?.campaignId}) - ${todayDate}`,
        });
        setCapture(false);
      }
    }
  };

  const [capture, setCapture] = useState(false);
  const fetchStoredQueries = async () => {
    try {
      const response = (await API.graphql({
        query: getQueries,
        variables: {
          campaign_id: activeCampaign?.campaignId?.toString(),
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetQueriesQuery };
      const storedQueries = response.data.getQueries;

      const filterStoredQueriesBasedOnDashType = (listofQueries: UserQuery[]) => {
        const queriesList = listofQueries.filter((q) => {
          const position = q?.position || '';
          return !(
            position.startsWith('3') ||
            // position.startsWith("TV3") ||
            // position.startsWith("Radio3") ||
            position.startsWith('OOH3') ||
            position.startsWith('Print3') ||
            position.startsWith('Cinema3')
          );
        });
        if (dashType === 'TV') {
          return queriesList?.filter((query) => query?.position?.startsWith('TV'));
        }
        if (dashType === 'RADIO') {
          return queriesList?.filter((query) => query?.position?.startsWith('Radio'));
        }
        if (dashType === 'OOH') {
          return queriesList?.filter((query) => query?.position?.startsWith('OOH'));
        }
        if (dashType === 'PRINT') {
          return queriesList?.filter((query) => query?.position?.startsWith('Print'));
        }
        if (dashType === 'CINEMA') {
          return queriesList?.filter((query) => query?.position?.startsWith('Cinema'));
        }
        if (dashType === 'DIGITAL' || dashType === 'INSOURCE_DATA') {
          return queriesList?.filter((query) => query?.position?.startsWith('1') || query?.position?.startsWith('2'));
        }
      };

      const filteredStoredQueries = filterStoredQueriesBasedOnDashType(storedQueries as UserQuery[]);

      const PDFElementss = filteredStoredQueries?.map((element) => {
        return {
          placeholder: element?.position as string,
          image: undefined,
          title: element?.title === 'Add title here' ? '' : (element?.title as string),
          comment: undefined,
          captured: false,
          columns: undefined,
          data: undefined,
        } as PDFelementType;
      });
      setPdfElements(PDFElementss as PDFelementType[]);
      setPPTElements(PDFElementss as PDFelementType[]);
    } catch (e) {
      console.log('ERROR FETCHING QUARIES', e);
    }
  };

  const getTitles = (placeholder: string) => {
    if (placeholder === 'TV1.1' || placeholder === 'Radio1.1') {
      return 'Station Split';
    } else if (placeholder === 'TV1.2' || placeholder === 'Radio1.2') {
      return '1+ Reach';
    } else if (placeholder === 'TV1.3' || placeholder === 'Radio1.3') {
      return 'TRP Per Week';
    } else if (placeholder === 'TV1.4' || placeholder === 'Radio1.4') {
      return 'Position in Break';
    } else if (placeholder === 'TV2.1' || placeholder === 'Radio2.1') {
      return 'Spot Variation';
    } else if (placeholder === 'TV2.2' || placeholder === 'Radio2.2') {
      return 'Timeslots';
    } else if (placeholder === 'OOH1.1' || placeholder === 'Print1.1' || placeholder === 'Cinema1.1') {
      return 'Overview';
    } else {
      return 'Add title here';
    }
  };

  async function fetchTemplateById(templateID: any) {
    setPPTElements([]);
    try {
      const response = (await API.graphql({
        query: getTemplateById,
        variables: {
          TemplateId: templateID,
          Client: {
            ClientCode: activeCampaign?.ClientCode,
            ClientCountry: activeCampaign?.ClientCountry,
          } as ClientsInput,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetTemplateByIdQuery };

      const placeholders = response.data.getTemplateById?.Placeholders;
      if (placeholders) {
        const PPTElements = placeholders.map((element) => {
          return {
            placeholder: element?.Placeholder as string,
            image: undefined,
            title: getTitles(element?.Placeholder as string),
            comment: undefined,
            captured: false,
            columns: undefined,
            data: undefined,
          } as PDFelementType;
        });
        setPPTElements(PPTElements as PDFelementType[]);
      }
    } catch (e) {
      console.log('ERROR FETCHING TEMPLATE BY ID', e);
    }
  }

  useEffect(() => {
    // Clear creative library sections when campaign changes
    setCreativeLibrarySections([]);

    if (activeCampaign) {
      if (dashType === 'DIGITAL' || dashType === 'INSOURCE_DATA') {
        fetchStoredQueries();
      } else if (activeCampaign && dashType === 'TV') {
        fetchTemplateById('c9614b9a-ed63-4a1c-9227-7d2c62247469');
      } else if (activeCampaign && dashType === 'RADIO') {
        fetchTemplateById('26126d4e-f347-4441-b36c-7ab7d3598f47');
      } else if (activeCampaign && dashType === 'OOH') {
        fetchTemplateById('7974c060-bad2-4048-9ce8-f9dbcc1a0753');
      } else if (activeCampaign && dashType === 'PRINT') {
        fetchTemplateById('fd723399-b637-408c-a77a-d69104022dde');
      } else if (activeCampaign && dashType === 'CINEMA') {
        fetchTemplateById('fd723399-b637-408c-a77a-kdfkgmsdfkgmsdf');
      }
    }
  }, [activeCampaign, dashType]);

  return (
    <InputContext.Provider
      value={{
        TVKPI,
        RADIOKPI,
        OOHKPI,
        PRINTKPI,
        CINEMAKPI,
        setNetworkList,
        networkList,
        setTVKPI,
        setRADIOKPI,
        setOOHKPI,
        setPRINTKPI,
        setCINEMAKPI,
        order2Table,
        setOrder2Table,
        order2Chart,
        setOrder2Chart,
        setCapture,
        capture,
        updatePPT,
        storedQueriesOnCurrentDash,
        setStoredQueriesOnCurrentDash,
        // childCampaignIndex,
        // setChildCampaignIndex,
        dataSourceLength,
        setDataSourceLength,
        organisationReportActive,
        setOrganisationReportViewActive,
        videoDashboardView,
        setVideoDashboardView: updateVideoDashboardView,
        contextedDataSources,
        setContextedDataSources,
        creativeLibrarySections,
        setCreativeLibrarySections,
        convertBackendCreativeToLocal,
        detectFileTypeFromName,
      }}
    >
      {props.children}
    </InputContext.Provider>
  );
};

export default InputContext;
